/**
 * RT ポップアップの「バックエンドデータ完全依存」移行用の純粋ロジック。
 *
 * - 表示情報（ロゴ/規約URL/送客キー等）は request_data 各行の直下フィールドから組み立てる。
 * - 表示判定は各行の *_arr（null許容）を使う（年齢・地域はバックエンド判定に委譲）。
 * - 副作用（ダイアログ emit）は呼び出し側に委ねるため、ここには含めない。
 */

import { matchesAdditionalDialogInfoGate } from '@/utils/realtime-transfer/rtAdditionalDialogInfoGate'

export interface RtConditionRow {
  client_id?: string
  permission_type?: number | string
  old_permission_type?: number | string | null
  dialog_name?: string
  dialog_type?: string | null
  request_able?: boolean
  display_order_num?: number | null
  age_range_mr?: string | null
  template_ids_arr?: string[] | null
  prefectures_arr?: string[] | null
  keyword_arr?: string[] | null
  exclude_keyword_arr?: string[] | null
  grade_year_arr?: Array<string | number> | null
  content?: string | null
  client_service_name?: string | null
  client_name?: string | null
  terms_url?: string | null
  privacy_url?: string | null
  // API レスポンスではオブジェクト or JSON 文字列 ('{"ApiEndpointKey":"sendJobs919"}') の両方が来る。
  additional_dialog_info?: Record<string, unknown> | string | null
  condition_id?: number | null
  google_conversation_id?: number | null
  google_conversation_label?: string | null
  api_endpoint_key?: string | null
  is_web_app?: boolean | number | string
  [key: string]: unknown
}

function getAdditionalDialogInfoFromRow(
  row: RtConditionRow
): Record<string, unknown> {
  return parseAdditionalDialogInfo(row.additional_dialog_info)
}

export interface RtConditionContext {
  formatId: string
  formatData: unknown
  basicData: { age?: number | string; [key: string]: unknown }
  // SP/PC 判定（MobileValid の表示ゲートで使用）。未指定時は PC 扱い。
  isMobileDevice?: boolean
  otherFormatData?: unknown // 相手書式（職務経歴書テンプレでは履歴書）。Hope*Valid 用
  // exclude_mail_domains 判定用（$firebase.user.email）。
  userEmail?: string | null
  addressCheck: (basicData: unknown, prefectures: string[]) => boolean
  keywordCheckRirekisho: (formatData: unknown, keywords: string[]) => boolean
  keywordCheckShokumukeirekisho: (
    formatData: unknown,
    keywords: string[]
  ) => boolean
  // additional_dialog_info.LicenseKeywords 用（資格欄のみのキーワード一致）。任意。
  keywordCheckOnlyLicense?: (
    formatId: string,
    formatData: unknown,
    keyword: string
  ) => boolean
}

export interface RtDialogInfo {
  clientId: string
  dialogName: string
  clientServiceName: string
  clientName: string
  logoImage: string
  termurl: string
  privacyurl: string
  sendApiEndpointKey?: string
  conditionId?: number | null
  is_web_app?: boolean
  googleConversationId?: number | null
  googleConversationLabel?: string | null
  [key: string]: unknown
}

/** 表示情報フィールドが空なら表示不可。 */
export function hasDisplayableRowInfo(row: RtConditionRow): boolean {
  return DISPLAY_RELATED_KEYS.some((key) => row[key] != null)
}
// prettier-ignore
const DISPLAY_RELATED_KEYS = 'content client_service_name client_name terms_url privacy_url dialog_type additional_dialog_info'.split(' ')

/** logoImage(content) がファイル名を持つ有効なパスか（'/static/.../.png' のような空名は無効）。 */
export function hasValidLogoImage(
  logoImage: string | null | undefined
): boolean {
  if (!logoImage || typeof logoImage !== 'string') return false
  const fileName = logoImage.split('/').pop() || ''
  // 拡張子の前にファイル名本体があること（'.png' だけは無効）。
  return /[^/.][^/]*\.[A-Za-z0-9]+$/.test(fileName)
}

/** auto_rt を除いた RT ダイアログ行のコピー（バックエンド配列順を保持）。 */
export function filterRtDialogRows(rows: unknown): RtConditionRow[] {
  const list = Array.isArray(rows)
    ? rows
    : rows && typeof rows === 'object'
      ? Object.values(rows as Record<string, unknown>)
      : []
  return list.filter((row): row is RtConditionRow => {
    if (!row || typeof row !== 'object') return false
    const name = (row as RtConditionRow).dialog_name
    return !!name && name !== 'auto_rt'
  })
}

/**
 * 行の条件配列で表示可否を判定する。各条件は任意で、null/空配列は「制約なし(通過)」。
 * keyword_arr（履歴書/職務経歴書いずれかでヒット）/ exclude_keyword_arr（ヒットで除外）/
 * grade_year_arr（formatData.gradeYear を含む）。template_ids_arr / prefectures_arr / age_range_mr はバックエンド判定に委譲。
 */
export function matchesRtRowCondition(
  row: RtConditionRow,
  ctx: RtConditionContext
): boolean {
  const add = getAdditionalDialogInfoFromRow(row)

  // forceDisplayFormat: 対象 formatId なら他条件を無視して強制表示（レガシーの
  // `if (formatId === '...') { isXxx = true }` 相当）。
  if (hasForceDisplayFormat(row, ctx.formatId)) return true

  // format_id（template_ids_arr）はバックエンド側でフィルタ済みとし、フロントでは判定しない。

  // 地域（prefectures_arr）はバックエンド側でフィルタ済みとし、フロントでは判定しない。

  const kws = row.keyword_arr
  if (Array.isArray(kws) && kws.length > 0) {
    let hit =
      ctx.keywordCheckRirekisho(ctx.formatData, kws) ||
      ctx.keywordCheckShokumukeirekisho(ctx.formatData, kws)
    // LicenseKeywords: 資格欄のみのキーワード一致を OR で許可（firstconnect 等）。
    if (!hit) hit = matchesLicenseKeywords(add, ctx)
    if (!hit) return false
  }

  const ngs = row.exclude_keyword_arr
  if (Array.isArray(ngs) && ngs.length > 0) {
    const hit =
      ctx.keywordCheckRirekisho(ctx.formatData, ngs) ||
      ctx.keywordCheckShokumukeirekisho(ctx.formatData, ngs)
    if (hit) return false
  }

  const grades = row.grade_year_arr
  if (Array.isArray(grades) && grades.length > 0) {
    const fd = ctx.formatData as { gradeYear?: unknown } | null | undefined
    const gradeYear = Number(fd ? fd.gradeYear : NaN)
    if (
      !Number.isFinite(gradeYear) ||
      !grades.map(Number).includes(gradeYear)
    ) {
      return false
    }
  }

  // 年齢（age_range_mr）はバックエンド側でフィルタ済みとし、フロントでは判定しない。

  // additional_dialog_info の入力検証ゲート（TelValid / MailValid など）。
  if (!matchesAdditionalDialogInfoGate(add, ctx)) return false
  return true
}

/** additional_dialog_info.LicenseKeywords のいずれかが資格欄に一致するか。 */
function matchesLicenseKeywords(
  add: Record<string, unknown>,
  ctx: RtConditionContext
): boolean {
  const list = add.LicenseKeywords
  if (!Array.isArray(list) || list.length === 0 || !ctx.keywordCheckOnlyLicense)
    return false
  return list.some(
    (kw) =>
      typeof kw === 'string' &&
      ctx.keywordCheckOnlyLicense!(ctx.formatId, ctx.formatData, kw)
  )
}

/** additional_dialog_info（オブジェクト or JSON 文字列）をオブジェクトに正規化する。 */
export function parseAdditionalDialogInfo(
  value: unknown
): Record<string, unknown> {
  if (!value) return {}
  if (typeof value === 'object') return value as Record<string, unknown>
  if (typeof value === 'string' && value.trim() !== '') {
    try {
      const parsed: unknown = JSON.parse(value)
      return parsed && typeof parsed === 'object'
        ? (parsed as Record<string, unknown>)
        : {}
    } catch (_e) {
      return {}
    }
  }
  return {}
}

/** 直下フィールドからダイアログ情報を組み立てる。 */
export function buildRtDialogInfo(row: RtConditionRow): RtDialogInfo {
  const add = getAdditionalDialogInfoFromRow(row)
  const isWebApp =
    row.is_web_app === true || row.is_web_app === 1 || row.is_web_app === '1'
  const info: RtDialogInfo = {
    ...add,
    clientId: String(row.client_id || ''),
    dialogName: String(row.dialog_name || ''),
    clientServiceName: String(row.client_service_name || ''),
    // backend に企業名フィールドが無いため暫定で client_service_name を流用
    clientName: String(row.client_name || ''),
    logoImage: String(row.content || ''),
    termurl: String(row.terms_url || ''),
    privacyurl: String(row.privacy_url || ''),
    is_web_app: isWebApp,
    // CVタグ判定（useGoogleAdsTag）用。auto_rt 経路（buildAutoRtDialogInfo）と同じマッピング。
    googleConversationId: (row.google_conversation_id as number | null) ?? null,
    googleConversationLabel:
      (row.google_conversation_label as string | null) ?? null
  }
  // api_endpoint_key はバックエンドの正規フィールド（additional_dialog_info.ApiEndpointKey の後継）。
  const apiKey = row.api_endpoint_key
  if (typeof apiKey === 'string' && apiKey.trim() !== '') {
    info.sendApiEndpointKey = apiKey
  }
  // condition_id: 新RTシステム行がダイアログ open 時に条件情報をフェッチするために使う。
  if (row.condition_id != null) info.conditionId = row.condition_id
  return info
}

/** dialog_type を解決（無ければ共通ダイアログ）。 */
export function resolveRtDialogType(row: RtConditionRow): string {
  return row.dialog_type || 'open-realtimetransfercommon-dialog'
}
/* ------------------------------------------------------------------ *
 * auto_rt（dialog_name === 'auto_rt'）専用の純データ駆動ロジック。
 * ------------------------------------------------------------------ */

export type AutoRtRequestableMode = 'NewOnly' | 'SetAutoRT'

export interface AutoRtRequestableSpec {
  mode: AutoRtRequestableMode
  newPermissionType: number
  oldPermissionType: number
}

export interface AutoRtDialogInfo {
  logoImage: string
  clientId: string
  dialogName: string
  clientName: string
  termurl: string
  privacyurl: string
  occupationurl: string
  handlingurl: string
  clientServiceName: string
  privacyLabel: string
  showTrailingBr: boolean
  permissionType: number
  oldPermissionType: number
  googleConversationId: number | null
  googleConversationLabel: string | null
  [key: string]: unknown
}

/**
 * auto_rt 行のみを抽出し display_order_num 昇順で返す。
 * 表示情報フィールドが空の行は API が非表示で返したものとしてスキップする。
 * 同一 display_order_num は元配列順を維持。
 */
export function sortedAutoRtRows(rows: unknown): RtConditionRow[] {
  const list = Array.isArray(rows)
    ? rows
    : rows && typeof rows === 'object'
      ? Object.values(rows as Record<string, unknown>)
      : []
  const autoRows = list.filter((row): row is RtConditionRow => {
    if (!row || typeof row !== 'object') return false
    const r = row as RtConditionRow
    if (r.dialog_name !== 'auto_rt') return false
    return hasDisplayableRowInfo(r)
  })
  const orderOf = (r: RtConditionRow): number => {
    const n = Number(r.display_order_num)
    return Number.isFinite(n) ? n : 0
  }
  return autoRows
    .map((row, idx) => ({ row, idx }))
    .sort((a, b) => orderOf(a.row) - orderOf(b.row) || a.idx - b.idx)
    .map((x) => x.row)
}

/** additional_dialog_info.forceDisplayFormat に formatId が含まれるか。 */
function hasForceDisplayFormat(row: RtConditionRow, formatId: string): boolean {
  const add = getAdditionalDialogInfoFromRow(row)
  const list = add.forceDisplayFormat
  return Array.isArray(list) && list.includes(formatId)
}

/**
 * requestable 方式（2択）を解決する。
 * - NewOnly: old_permission_type が null/未設定、または NotSendOldPermission === true。
 * - SetAutoRT: old_permission_type 設定あり かつ NotSendOldPermission 無し。
 */
export function resolveRequestableMode(
  row: RtConditionRow
): AutoRtRequestableSpec {
  const add = getAdditionalDialogInfoFromRow(row)
  const newPermissionType = Number(row.permission_type) || 0
  const oldRaw = row.old_permission_type
  const isNewOnly = oldRaw == null || add.NotSendOldPermission === true
  if (isNewOnly) {
    return { mode: 'NewOnly', newPermissionType, oldPermissionType: 0 }
  }
  return {
    mode: 'SetAutoRT',
    newPermissionType,
    oldPermissionType: Number(oldRaw) || 0
  }
}

type RequestableRow = {
  client_id?: unknown
  permission_type?: unknown
  request_able?: boolean
}

/**
 * client_id + permission_type の request_able 判定。
 * レスポンスに該当行が無い場合は未制限（true）とみなす。
 * （global-var.js の isRtConditionEnabled と同じ「行欠落 = 未制限」方針）
 */
export function hasRequestablePermission(
  rows: RequestableRow[],
  clientId: string,
  permissionType: number
): boolean {
  const matched = rows.find(
    (item) =>
      String(item.client_id) === String(clientId) &&
      Number(item.permission_type) === Number(permissionType)
  )
  if (!matched) return true
  return matched.request_able !== false
}

/**
 * SetAutoRT の requestable 判定。
 * - 新 permission は requestable 必須（行欠落は未制限）
 * - check_flag !== '1': 旧 permission の requestable は要求しない
 *   （API に旧行が無い / 他 permission 送客済みでも新が requestable なら表示可）
 * - check_flag === '1': 旧も requestable 必須（行欠落は未制限）
 */
export function isSetAutoRtRequestable(
  rows: RequestableRow[],
  clientId: string,
  newPermissionType: number,
  oldPermissionType: number,
  checkFlag: unknown
): boolean {
  if (!hasRequestablePermission(rows, clientId, newPermissionType)) return false
  if (String(checkFlag) !== '1') return true
  return hasRequestablePermission(rows, clientId, oldPermissionType)
}

/**
 * resolveRequestableMode 結果に対する requestable 可否。
 */
export function isAutoRtRequestable(
  rows: RequestableRow[],
  clientId: string,
  spec: AutoRtRequestableSpec,
  checkFlag: unknown
): boolean {
  if (spec.mode === 'NewOnly') {
    return hasRequestablePermission(rows, clientId, spec.newPermissionType)
  }
  return isSetAutoRtRequestable(
    rows,
    clientId,
    spec.newPermissionType,
    spec.oldPermissionType,
    checkFlag
  )
}

export function buildAutoRtDialogInfo(row: RtConditionRow): AutoRtDialogInfo {
  const add = getAdditionalDialogInfoFromRow(row)
  const spec = resolveRequestableMode(row)
  const info: AutoRtDialogInfo = {
    logoImage: String(row.content || ''),
    clientId: String(row.client_id || ''),
    dialogName: String(row.dialog_name || 'auto_rt'),
    clientName: String(row.client_name || ''),
    termurl: String(row.terms_url || ''),
    privacyurl: String(row.privacy_url || ''),
    occupationurl:
      typeof add.occupationurl === 'string' ? add.occupationurl : '',
    handlingurl: typeof add.handlingurl === 'string' ? add.handlingurl : '',
    clientServiceName: String(row.client_service_name || ''),
    privacyLabel: typeof add.privacyLabel === 'string' ? add.privacyLabel : '',
    showTrailingBr: add.showTrailingBr === true,
    partner_terms_text:
      typeof add.partner_terms_text === 'string' ? add.partner_terms_text : '',
    hide_yagish_terms: add.hide_yagish_terms === true,
    // 完了画面のロゴは既定で出さない。次のどちらかが true の行だけ出す。
    // autoRtLogoVisibleGate: ロゴ表示そのもの（例: mynavi_api / 1）
    // foreverHistoryGate: 送客履歴を無期限で見る行。DL_RT の提携ロゴも残す
    autoRtLogoVisibleGate: add.autoRtLogoVisibleGate === true,
    foreverHistoryGate: add.foreverHistoryGate === true,
    permissionType: spec.newPermissionType,
    oldPermissionType: spec.oldPermissionType,
    googleConversationId: (row.google_conversation_id as number | null) ?? null,
    googleConversationLabel:
      (row.google_conversation_label as string | null) ?? null
  }
  const apiKey = row.api_endpoint_key
  if (typeof apiKey === 'string' && apiKey.trim() !== '') {
    info.sendApiEndpointKey = apiKey
  }
  return info
}
